import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminGetReferralDefaults,
  adminPutReferralDefaults,
} from "@/lib/referrals";

/**
 * Admin "Referral rewards" card — sits inside /admin/payment-settings.
 *
 * Existing referrals capture a snapshot of these amounts at creation time,
 * so changes here only affect future links.
 */
export default function ReferralDefaultsCard() {
  const [defs, setDefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminGetReferralDefaults()
      .then(setDefs)
      .catch(() => setDefs(null));
  }, []);

  if (!defs) return null;

  const onChange = (k, v) => setDefs((d) => ({ ...d, [k]: v }));

  const onSave = async () => {
    setBusy(true);
    try {
      const next = await adminPutReferralDefaults({
        referrer_bonus_inr: Number(defs.referrer_bonus_inr),
        referrer_bonus_usd: Number(defs.referrer_bonus_usd),
        referred_bonus_inr: Number(defs.referred_bonus_inr),
        referred_bonus_usd: Number(defs.referred_bonus_usd),
      });
      setDefs(next);
      toast.success("Referral rewards saved");
    } catch (err) {
      const d = err?.response?.data?.detail || "Failed";
      toast.error(typeof d === "string" ? d : "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      className="border-stone-200"
      data-testid="referral-defaults-card"
    >
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Gift className="h-4 w-4 text-amber-600" /> Referral rewards
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-stone-500">
          Bonus amounts credited when a referred student completes their
          first payment. Existing referral links use the snapshot captured
          at share time — changes here only apply to <em>future</em> links.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Referrer bonus (₹)</Label>
            <Input
              type="number"
              min={0}
              value={defs.referrer_bonus_inr}
              onChange={(e) => onChange("referrer_bonus_inr", e.target.value)}
              data-testid="ref-referrer-inr"
            />
          </div>
          <div className="space-y-1">
            <Label>Referrer bonus ($)</Label>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={defs.referrer_bonus_usd}
              onChange={(e) => onChange("referrer_bonus_usd", e.target.value)}
              data-testid="ref-referrer-usd"
            />
          </div>
          <div className="space-y-1">
            <Label>Referred bonus (₹)</Label>
            <Input
              type="number"
              min={0}
              value={defs.referred_bonus_inr}
              onChange={(e) => onChange("referred_bonus_inr", e.target.value)}
              data-testid="ref-referred-inr"
            />
          </div>
          <div className="space-y-1">
            <Label>Referred bonus ($)</Label>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={defs.referred_bonus_usd}
              onChange={(e) => onChange("referred_bonus_usd", e.target.value)}
              data-testid="ref-referred-usd"
            />
          </div>
        </div>
        <Button
          onClick={onSave}
          disabled={busy}
          className="bg-stone-900 hover:bg-stone-800 text-white"
          data-testid="ref-save-defaults"
        >
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
          Save rewards
        </Button>
      </CardContent>
    </Card>
  );
}
